import { lazy } from 'react';

// Route pages are code-split, so a dynamic import() for a page the user hadn't
// visited yet 404s (ChunkLoadError) after a deploy — the old content-hashed chunk
// files are gone from the server. Suspense doesn't catch import rejections; the
// nearest ErrorBoundary does, which would drop the user on FullPageError.
//
// Instead, reload the page once so the browser fetches the fresh index.html + chunk
// manifest. The sessionStorage flag prevents a reload loop if the failure is genuine
// (e.g. the chunk really is broken) — on the second failure we rethrow and let the
// ErrorBoundary take over.
const RELOAD_FLAG = 'lazy-chunk-reloaded';

// Same module shape React.lazy itself accepts.
type LazyComponentModule = Awaited<ReturnType<Parameters<typeof lazy>[0]>>;

export function lazyWithRetry(factory: () => Promise<LazyComponentModule>) {
  return lazy(async () => {
    try {
      const component = await factory();
      window.sessionStorage.removeItem(RELOAD_FLAG);
      return component;
    } catch (error) {
      if (!window.sessionStorage.getItem(RELOAD_FLAG)) {
        window.sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
        // Keep Suspense on its (null) fallback until the reload swaps the document,
        // rather than surfacing the error mid-reload.
        return new Promise<LazyComponentModule>(() => {});
      }
      throw error;
    }
  });
}
